import React, { useState, useEffect, useRef } from 'react';
import IASideBar from './IASideBar';
import * as mui from '@mui/material';
import ResponsiveContainer from '../../layout/Container';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import SecurityIcon from '@mui/icons-material/Security';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import DescriptionIcon from '@mui/icons-material/Description';
import HomeIcon from '@mui/icons-material/Home';
import ShieldIcon from '@mui/icons-material/Shield';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SettingsIcon from '@mui/icons-material/Settings';
import LockIcon from '@mui/icons-material/Lock';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import appService from '../../../services/ApplicationService';
import { styled } from '@mui/material/styles';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import * as XLSX from 'xlsx';
import Autocomplete from '@mui/material/Autocomplete';
import LogoLoader from '../../common/LogoLoader';

const SODLibrary = () => {
  // Initial loading state
  const [initialLoading, setInitialLoading] = useState(true);

  // Application selection states
  const [applications, setApplications] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [appDialogOpen, setAppDialogOpen] = useState(false);
  const [appSearchTerm, setAppSearchTerm] = useState('');
  const [appFilterCompany, setAppFilterCompany] = useState('');
  
  // SOD rule management states
  const [sodRules, setSodRules] = useState([]);
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [currentRule, setCurrentRule] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Role analysis states
  const [roleData, setRoleData] = useState({ roles: [], userRolePairs: new Map() });
  const [rolesLoading, setRolesLoading] = useState(false);
  
  // AI suggestion states
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  
  // File handling states
  const [fileUploadError, setFileUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInputRef = useRef(null);
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [dialogData, setDialogData] = useState(null);

  // Legacy states
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // State to track applications with roles
  const [appsWithRoles, setAppsWithRoles] = useState(new Set());

  // Additional loading states
  const [isUploading, setIsUploading] = useState(false);

  // Additional state for handling multi-selection of AI suggestions
  const [selectedSuggestions, setSelectedSuggestions] = useState([]);

  const handleOpenDialog = (type, data = null) => {
    setDialogType(type);
    setDialogData(data);
    setOpenDialog(true);
  };
  
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDialogType('');
    setDialogData(null);
    setSubmitted(false);
  };
  
  // // Legacy submit handler
  const handleSubmit = () => {
    console.log('Request submitted:', { name, email });
    setSubmitted(true);
    setTimeout(() => {
      setOpenDialog(false);
      setSubmitted(false);
    }, 2000);
  };
  
  // File input styling
  const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
  });

  // Create a theme consistent with your application
  const theme = createTheme({
    palette: {
      primary: {
        main: '#0288d1',
      },
      secondary: {
        main: '#4caf50',
      },
      error: {
        main: '#d32f2f',
      },
      warning: {
        main: '#ed6c02',
      },
      info: {
        main: '#0288d1',
      },
      success: {
        main: '#2e7d32',
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
            fontWeight: 500,
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: 'none',
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: 'rgba(2, 136, 209, 0.04)',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600,
            },
          },
        },
      },
    },
  });

  // Fetch applications on component mount
  useEffect(() => {
    const fetchApplications = async () => {
      setInitialLoading(true);
      setIsLoading(true);
      try {
        const response = await appService.fetchApps();
        setApplications(response || []);
        
        // Fetch role counts for each application to identify apps with roles
        if (response && response.length > 0) {
          const appsWithRolesSet = new Set();
          for (const app of response) {
            try {
              const rolesData = await appService.fetchAppsRecordById(app.id);
              // Count active users with roles
              const hasRoles = rolesData.some(user => user.STATUS === 'Active' && user.ROLE_NAME);
              if (hasRoles) {
                appsWithRolesSet.add(app.id);
              }
            } catch (error) {
              console.error(`Error fetching roles for app ${app.id}:`, error);
            }
          }
          setAppsWithRoles(appsWithRolesSet);
        }
      } catch (error) {
        console.error('Error fetching applications:', error);
      } finally {
        setIsLoading(false);
        // Add a small delay to make the logo loader visible
        setTimeout(() => {
          setInitialLoading(false);
        }, 800);
      }
    };

    fetchApplications();
  }, []);

  // Fetch roles data for the selected app
  const fetchRolesData = async (appId) => {
    if (!appId) return;
    
    setRolesLoading(true);
    try {
      const response = await appService.fetchAppsRecordById(appId);
      
      // Group by ROLE_NAME and count
      const roleGroups = {};
      const userRolePairs = new Map(); // Track which users have which roles
      
      response.forEach(user => {
        const role = user.ROLE_NAME || 'Unassigned';
        // Skip inactive users
        if (user.STATUS !== 'Active') return;
        
        // Track this user's role for conflict counting
        const userId = user.USER_ID || user.EMAIL_ADDRESS;
        if (userId) {
          if (!userRolePairs.has(userId)) {
            userRolePairs.set(userId, new Set());
          }
          userRolePairs.get(userId).add(role);
        }
        
        if (!roleGroups[role]) {
          roleGroups[role] = {
            role: role,
            count: 0,
            users: []
          };
        }
        
        roleGroups[role].count++;
        roleGroups[role].users.push({
          userId: user.USER_ID,
          email: user.EMAIL_ADDRESS,
          firstName: user.FIRST_NAME,
          lastName: user.LAST_NAME
        });
      });
      
      // Convert to array and sort by count
      const roleDataArray = Object.values(roleGroups)
        .sort((a, b) => b.count - a.count)
        .map(role => ({
          role: role.role,
          count: role.count,
          users: role.users
        }));
      
      setRoleData({
        roles: roleDataArray,
        userRolePairs: userRolePairs
      });
      
      // After loading role data, clear any previous AI suggestions
      setAiSuggestions([]);
    } catch (error) {
      console.error('Error fetching roles data:', error);
    } finally {
      setRolesLoading(false);
    }
  };

  // Count users that have both roles (conflict count)
  const countUsersWithConflict = (role1, role2) => {
    if (!roleData || !roleData.userRolePairs) return 0;
    
    let conflictCount = 0;
    roleData.userRolePairs.forEach((roles, userId) => {
      if (roles.has(role1) && roles.has(role2)) {
        conflictCount++;
      }
    });
    
    return conflictCount;
  };

  // Enhance AI suggestions with conflict counts
  const enhanceSuggestionsWithCounts = (suggestions) => {
    return suggestions.map(suggestion => ({
      ...suggestion,
      conflictCount: countUsersWithConflict(suggestion.role1, suggestion.role2)
    }));
  };

  // Filter applications based on search, company filter, and having roles
  const filteredApps = applications.filter(app => {
    const matchesSearch = app.APP_NAME.toLowerCase().includes(appSearchTerm.toLowerCase()) ||
      (app.COMPANY_NAME && app.COMPANY_NAME.toLowerCase().includes(appSearchTerm.toLowerCase()));
    
    const matchesCompany = !appFilterCompany || app.COMPANY_NAME === appFilterCompany;
    
    // Only include apps that have roles
    const hasRoles = appsWithRoles.has(app.id);
    
    return matchesSearch && matchesCompany && hasRoles;
  });
  
  // Get unique company names for filter
  const companyNames = [...new Set(applications
    .filter(app => app.COMPANY_NAME)
    .map(app => app.COMPANY_NAME)
  )].sort();

  // Handle opening app selection dialog
  const handleOpenAppDialog = () => {
    setAppDialogOpen(true);
    setAppSearchTerm('');
    setAppFilterCompany('');
  };
  
  // Handle selecting an app from the dialog
  const handleSelectApp = (app) => {
    setSelectedApp(app);
    setAppDialogOpen(false);
    
    // Fetch SOD rules for the selected app
    if (app) {
      fetchSODRules(app.id);
      fetchRolesData(app.id);
    }
  };

  // Fetch SOD rules for a specific app
  const fetchSODRules = async (appId) => {
    setIsLoading(true);
    try {
      const response = await appService.fetchSODRulesByApp(appId);
      // Transform the API response to match our data grid format
      const formattedRules = response.map(rule => ({
        id: rule.id,
        role1: rule.ROLE1,
        role2: rule.ROLE2,
        conflictType: rule.CONFLICT_TYPE,
        description: rule.DESCRIPTION,
        createdAt: new Date(rule.CREATED_ON).toISOString().split('T')[0],
        isActive: rule.IS_ACTIVE
      }));
      setSodRules(formattedRules);
    } catch (error) {
      console.error('Error fetching SOD rules:', error);
      setSodRules([]);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle rule editing
  const handleEditRule = (rule) => {
    setCurrentRule(rule);
    setIsEditMode(true);
    setShowRuleForm(true);
  };
  
  // Handle rule addition
  const handleAddRule = () => {
    setCurrentRule({
      id: null, // Will be generated by the backend
      role1: '',
      role2: '',
      conflictType: 'Medium',
      description: '',
      createdAt: new Date().toISOString().split('T')[0]
    });
    setIsEditMode(false);
    setShowRuleForm(true);
  };
  
  // Handle rule deletion
  const handleDeleteRule = async () => {
    try {
      await appService.deleteSODRule(dialogData.id);
      const updatedRules = sodRules.filter(rule => rule.id !== dialogData.id);
      setSodRules(updatedRules);
      handleCloseDialog();
    } catch (error) {
      console.error('Error deleting rule:', error);
      setFileUploadError('Failed to delete the rule. Please try again.');
    }
  };
  
  // Check if a rule with the same role combination already exists
  const checkRuleExists = (role1, role2) => {
    if (!sodRules || !role1 || !role2) return false;
    
    // Check if this role combination already exists in either direction
    return sodRules.some(rule => 
      (rule.role1 === role1 && rule.role2 === role2) || 
      (rule.role1 === role2 && rule.role2 === role1)
    );
  };
  
  // Handle saving rule (both new and edit)
  const handleSaveRule = async (ruleData) => {
    console.log("Starting handleSaveRule with data:", ruleData);
    
    if (!selectedApp || !selectedApp.id) {
      console.error("No application selected");
      setFileUploadError('Please select an application first');
      return;
    }
    
    if (!ruleData.role1 || !ruleData.role2) {
      console.error("Missing required fields");
      setFileUploadError('Role 1 and Role 2 are required fields');
      return;
    }
    
    if (ruleData.role1 === ruleData.role2) {
      console.error("Same role selected for both fields");
      setFileUploadError('Role 1 and Role 2 cannot be the same');
      return;
    }
    
    // Check if rule already exists (for new rules only)
    if (!isEditMode && checkRuleExists(ruleData.role1, ruleData.role2)) {
      console.error("Rule already exists for this role combination");
      setFileUploadError('A rule for this role combination already exists');
      return;
    }
    
    try {
      if (isEditMode) {
        // Map the form data to the API format
        const apiData = {
          APP_NAME: selectedApp.id,
          ROLE1: ruleData.role1,
          ROLE2: ruleData.role2,
          CONFLICT_TYPE: ruleData.conflictType,
          DESCRIPTION: ruleData.description
        };
        
        console.log("Updating existing rule:", apiData);
        
        // Update existing rule
        await appService.updateSODRule(ruleData.id, apiData);
        
        const updatedRules = sodRules.map(rule => 
          rule.id === ruleData.id ? ruleData : rule
        );
        setSodRules(updatedRules);
        
        // Update AI suggestions if any exist - filter out the updated rule
        if (aiSuggestions.length > 0) {
          const updatedSuggestions = aiSuggestions.filter(suggestion =>
            !((suggestion.role1 === ruleData.role1 && suggestion.role2 === ruleData.role2) ||
              (suggestion.role1 === ruleData.role2 && suggestion.role2 === ruleData.role1))
          );
          setAiSuggestions(updatedSuggestions);
        }
      } else {
        // Map the form data to the API format
        const apiData = {
          APP_NAME: selectedApp.id,
          ROLE1: ruleData.role1,
          ROLE2: ruleData.role2,
          CONFLICT_TYPE: ruleData.conflictType,
          DESCRIPTION: ruleData.description
        };
        
        console.log("Creating new rule with data:", apiData);
        
        // Add new rule
        const response = await appService.createSODRule(apiData);
        
        console.log("API response:", response);
        
        // Transform the API response to match our data grid format
        const newRule = {
          id: response.id || new Date().getTime(), // Fallback ID if missing
          role1: response.ROLE1 || ruleData.role1, 
          role2: response.ROLE2 || ruleData.role2,
          conflictType: response.CONFLICT_TYPE || ruleData.conflictType,
          description: response.DESCRIPTION || ruleData.description,
          createdAt: response.CREATED_ON ? new Date(response.CREATED_ON).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
        };
        
        console.log("Adding new rule to UI:", newRule);
        
        // Update the rules list with the new rule
        setSodRules(prevRules => [...prevRules, newRule]);
        
        // Update AI suggestions if any exist - filter out the newly added rule
        if (aiSuggestions.length > 0) {
          const updatedSuggestions = aiSuggestions.filter(suggestion =>
            !((suggestion.role1 === newRule.role1 && suggestion.role2 === newRule.role2) ||
              (suggestion.role1 === newRule.role2 && suggestion.role2 === newRule.role1))
          );
          setAiSuggestions(updatedSuggestions);
        }
      }
      
      // Close the form and reset state
      setShowRuleForm(false);
      setCurrentRule(null);
      
      // Display success message
      setUploadSuccess(true);
      setTimeout(() => {
        setUploadSuccess(false);
      }, 3000);
      
    } catch (error) {
      console.error('Error saving rule:', error);
      setFileUploadError(`Failed to save the rule: ${error.message || 'Unknown error'}`);
      // Leave the form open so the user can try again
    }
  };
  
  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    setFileUploadError('');
    setUploadSuccess(false);
    
    if (!file) return;
    
    // Check file type
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls') && !file.name.endsWith('.csv')) {
      setFileUploadError('Please upload an Excel or CSV file');
      return;
    }
    
    // Set uploading state to true to show animation
    setIsUploading(true);
    
    const reader = new FileReader();
    
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        // Validate the data structure
        if (jsonData.length === 0) {
          setFileUploadError('The file contains no data');
          setIsUploading(false);
          return;
        }
        
        // Check for required fields
        const requiredFields = ['role1', 'role2', 'conflictType', 'description'];
        const firstRow = jsonData[0];
        
        // Normalize headers to account for different casing/formats
        const normalizedHeaders = Object.keys(firstRow).map(key => key.toLowerCase());
        
        const missingFields = requiredFields.filter(field => 
          !normalizedHeaders.some(header => 
            header === field.toLowerCase() || 
            header === field.replace(/([A-Z])/g, '_$1').toLowerCase() ||
            header === field.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()
          )
        );
        
        if (missingFields.length > 0) {
          setFileUploadError(`Missing required fields: ${missingFields.join(', ')}`);
          setIsUploading(false);
          return;
        }
        
        // Validate conflict severity values
        const validSeverities = ['Critical', 'High', 'Medium', 'Low'];
        const invalidSeverities = [];
        
        for (let i = 0; i < jsonData.length; i++) {
          const row = jsonData[i];
          
          // Skip metadata/instruction rows
          if (row.role1 === 'INSTRUCTIONS' || 
              row.Role1 === 'INSTRUCTIONS' || 
              row['Role 1'] === 'INSTRUCTIONS') {
            continue;
          }
          
          // Get normalized conflict type value regardless of casing
          const getConflictTypeValue = () => {
            const variants = [
              'conflictType', 
              'ConflictType',
              'conflict_type',
              'Conflict Type',
              'CONFLICT_TYPE'
            ];
            
            for (const variant of variants) {
              if (row[variant] !== undefined) return row[variant];
            }
            return '';
          };
          
          const conflictType = getConflictTypeValue();
          
          // Skip empty rows or rows without conflict type
          if (!conflictType) continue;
          
          // Check if the value is valid (case-insensitive comparison)
          const isValid = validSeverities.some(
            severity => severity.toLowerCase() === conflictType.toLowerCase()
          );
          
          if (!isValid) {
            invalidSeverities.push({
              row: i + 2, // +2 for Excel row number (1-indexed + header row)
              value: conflictType
            });
            
            // Limit the number of errors we report to prevent overwhelming messages
            if (invalidSeverities.length >= 3) break;
          }
        }
        
        // If invalid severities found, stop the upload
        if (invalidSeverities.length > 0) {
          const errorMessage = `Invalid conflict severity values found:
          ${invalidSeverities.map(item => `Row ${item.row}: "${item.value}"`).join(', ')}.
          Valid values are: ${validSeverities.join(', ')}.`;
          
          setFileUploadError(errorMessage);
          setIsUploading(false);
          return;
        }
        
        // Process and upload each rule
        let successCount = 0;
        let failureCount = 0;
        let duplicateCount = 0;
        
        for (const row of jsonData) {
          try {
            // Skip metadata/instruction rows (if present in template)
            if (row.role1 === 'INSTRUCTIONS' || 
                row.Role1 === 'INSTRUCTIONS' || 
                row['Role 1'] === 'INSTRUCTIONS') {
              continue;
            }
            
            // Get normalized field values regardless of casing
            const getFieldValue = (fieldName) => {
              const variants = [
                fieldName, 
                fieldName.charAt(0).toUpperCase() + fieldName.slice(1),
                fieldName.replace(/([a-z])([A-Z])/g, '$1 $2'),
                fieldName.replace(/([a-z])([A-Z])/g, '$1_$2')
              ];
              
              for (const variant of variants) {
                if (row[variant] !== undefined) return row[variant];
              }
              return '';
            };
            
            const role1 = getFieldValue('role1');
            const role2 = getFieldValue('role2');
            let conflictType = getFieldValue('conflictType') || 'Medium';
            const description = getFieldValue('description');
            
            // Skip empty rows
            if (!role1 || !role2) continue;
            
            // Normalize conflict type case to match expected values
            validSeverities.forEach(severity => {
              if (conflictType.toLowerCase() === severity.toLowerCase()) {
                conflictType = severity; // Use proper casing
              }
            });
            
            // Check if this rule already exists
            const isDuplicate = checkRuleExists(role1, role2);
            
            if (isDuplicate) {
              duplicateCount++;
              continue;
            }
            
            // Map the row data to the API format
            const apiData = {
              APP_NAME: selectedApp.id,
              ROLE1: role1,
              ROLE2: role2,
              CONFLICT_TYPE: conflictType,
              DESCRIPTION: description
            };
            
            await appService.createSODRule(apiData);
            successCount++;
          } catch (error) {
            console.error('Error creating rule from file:', error);
            failureCount++;
          }
        }
        
        // Fetch updated rules
        await fetchSODRules(selectedApp.id);
        
        setUploadSuccess(true);
        
        // Provide detailed feedback
        if (duplicateCount > 0) {
          setFileUploadError(
            `Successfully added ${successCount} rules. ` + 
            `Skipped ${duplicateCount} duplicate rules. ` + 
            (failureCount > 0 ? `Failed to add ${failureCount} rules.` : '')
          );
        } else if (failureCount > 0) {
          setFileUploadError(`Successfully added ${successCount} rules, failed to add ${failureCount} rules.`);
        }
        
        // Reset the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        // Hide success message after 3 seconds
        setTimeout(() => {
          setUploadSuccess(false);
        }, 3000);
        
      } catch (error) {
        console.error('Error processing file:', error);
        setFileUploadError('Error processing file. Please check the format and try again.');
      } finally {
        setIsUploading(false);
      }
    };
    
    reader.onerror = () => {
      setFileUploadError('Error reading file');
      setIsUploading(false);
    };
    
    reader.readAsArrayBuffer(file);
  };
  
  // Generate and download template
  const handleDownloadTemplate = () => {
    // Check if we have role data available
    const hasRoles = roleData && roleData.roles.length > 0;
    const topRoles = hasRoles ? 
      roleData.roles.filter(role => role.count > 0).slice(0, 5).map(r => r.role) : 
      [];
    
    // Create example conflicts based on available roles
    let templateData = [];
    
    if (topRoles.length >= 2) {
      // Use actual roles if available
      templateData.push({
        role1: topRoles[0],
        role2: topRoles[1],
        conflictType: 'Critical',
        description: `System ${topRoles[0]} should not have ${topRoles[1]} responsibilities`
      });
      
      if (topRoles.length >= 4) {
        templateData.push({
          role1: topRoles[2],
          role2: topRoles[3],
          conflictType: 'High',
          description: `Segregate ${topRoles[2]} and ${topRoles[3]} responsibilities`
        });
      }
    }
    
    // Add generic examples if we don't have enough role data
    if (templateData.length < 2) {
      templateData = [
        {
          role1: 'Administrator',
          role2: 'Financial Approver',
          conflictType: 'Critical',
          description: 'System admin should not approve financial transactions'
        },
        {
          role1: 'Purchase Order Creator',
          role2: 'Purchase Order Approver',
          conflictType: 'High',
          description: 'Segregate purchase order creation and approval'
        }
      ];
    }
    
    // Add explanatory instructions as metadata
    const instructions = [
      {
        role1: 'INSTRUCTIONS',
        role2: 'INSTRUCTIONS',
        conflictType: 'INSTRUCTIONS',
        description: 'Below is the list of available roles in this application that you can use for creating SOD rules'
      }
    ];
    
    // If we have roles, add them as examples
    const rolesList = hasRoles ? 
      roleData.roles.map(role => ({
        role1: role.role,
        role2: '',
        conflictType: `Users: ${role.count}`,
        description: `This role has ${role.count} users`
      })) : [];
    
    // Combine instructions, role list and template data
    const completeTemplate = [...instructions, ...rolesList, ...templateData];
    
    // Create a worksheet from the template data
    const worksheet = XLSX.utils.json_to_sheet(completeTemplate);
    
    // Add column width specifications
    const wscols = [
      { wch: 25 }, // role1
      { wch: 25 }, // role2
      { wch: 15 }, // conflictType
      { wch: 50 }  // description
    ];
    worksheet['!cols'] = wscols;
    
    // Create a workbook with the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'SOD Rules Template');
    
    // Generate file name with app name if available
    const fileName = selectedApp ? 
      `${selectedApp.APP_NAME.replace(/\s+/g, '_')}_sod_rules_template.xlsx` : 
      'sod_rules_template.xlsx';
    
    // Generate and download the file
    XLSX.writeFile(workbook, fileName);
  };

  // Find suggested SOD rules using AI
  const getAISuggestedRules = async () => {
    if (!selectedApp || !selectedApp.id) return;
    
    // Validate that there are roles available
    if (roleData.roles.length === 0) {
      setAnalysisError('No roles are available to analyze. Please add roles to this application first.');
      return;
    }
    
    setIsAnalyzing(true);
    setAnalysisError(null);
    
    try {
      // Modified to specifically request SOX-related conflicts
      const response = await appService.analyzeSODRules(selectedApp.id, {
        focus: 'SOX', // Add parameter to specify SOX focus
        exclude: 'operational' // Exclude operational conflicts
      });
      
      if (response && response.suggestions) {
        // Transform the suggestions to match our format
        const formattedSuggestions = response.suggestions.map(suggestion => ({
          role1: suggestion.role1,
          role2: suggestion.role2,
          severity: suggestion.severity,
          explanation: suggestion.explanation,
          soxCategory: suggestion.soxCategory || 'Financial Reporting' // Add SOX category
        }));
        
        // Filter out suggestions that already exist in the rules
        const filteredSuggestions = formattedSuggestions.filter(suggestion => {
          // Check if this role combination already exists in either direction
          return !sodRules.some(rule => 
            (rule.role1 === suggestion.role1 && rule.role2 === suggestion.role2) || 
            (rule.role1 === suggestion.role2 && rule.role2 === suggestion.role1)
          );
        });
        
        console.log(`Filtered out ${formattedSuggestions.length - filteredSuggestions.length} suggestions that already exist as rules`);
        
        // Enhance suggestions with conflict counts
        const enhancedSuggestions = enhanceSuggestionsWithCounts(filteredSuggestions);
        
        // Sort by conflict count (highest first) then by severity
        const sortedSuggestions = enhancedSuggestions.sort((a, b) => {
          // First sort by conflict count (descending)
          if (b.conflictCount !== a.conflictCount) {
            return b.conflictCount - a.conflictCount;
          }
          
          // Then by severity (Critical > High)
          const severityOrder = { 'Critical': 0, 'High': 1 };
          return severityOrder[a.severity] - severityOrder[b.severity];
        });
        
        setAiSuggestions(sortedSuggestions);
      } else {
        setAiSuggestions([]);
      }
    } catch (error) {
      console.error('Error getting AI suggestions:', error);
      setAnalysisError('Failed to analyze roles. Please try again.');
      setAiSuggestions([]);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Handle adding a suggested rule
  const handleAddSuggestedRule = (suggestion) => {
    // Create a rule object from the suggestion
    const newRule = {
      id: null, // Will be generated by the backend
      role1: suggestion.role1,
      role2: suggestion.role2,
      conflictType: suggestion.severity,
      description: `${suggestion.soxCategory ? '[' + suggestion.soxCategory + '] ' : ''}${suggestion.explanation || `SOX compliance rule between ${suggestion.role1} and ${suggestion.role2}`}`,
      createdAt: new Date().toISOString().split('T')[0]
    };
    
    // Set the current rule for the form
    setCurrentRule(newRule);
    setIsEditMode(false);
    setShowRuleForm(true);
  };

  // Handle selecting/deselecting an individual suggestion
  const handleSelectSuggestion = (index) => {
    setSelectedSuggestions(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };
  
  // Handle select all / deselect all suggestions
  const handleSelectAllSuggestions = () => {
    if (selectedSuggestions.length === aiSuggestions.length) {
      // Deselect all
      setSelectedSuggestions([]);
    } else {
      // Select all
      setSelectedSuggestions(aiSuggestions.map((_, index) => index));
    }
  };

  // Add multiple suggested rules at once
  const handleAddMultipleSuggestedRules = async () => {
    if (selectedSuggestions.length === 0 || !selectedApp) return;
    
    setIsAnalyzing(true); // Reuse this state to show loading
    
    try {
      let successCount = 0;
      let failureCount = 0;
      let duplicateCount = 0;
      
      for (const suggestionIndex of selectedSuggestions) {
        const suggestion = aiSuggestions[suggestionIndex];
        
        // Check if rule already exists
        const isDuplicate = checkRuleExists(suggestion.role1, suggestion.role2);
        if (isDuplicate) {
          duplicateCount++;
          continue;
        }
        
        // Create API data
        const apiData = {
          APP_NAME: selectedApp.id,
          ROLE1: suggestion.role1,
          ROLE2: suggestion.role2,
          CONFLICT_TYPE: suggestion.severity,
          DESCRIPTION: `${suggestion.soxCategory ? '[' + suggestion.soxCategory + '] ' : ''}${suggestion.explanation || `SOX compliance rule between ${suggestion.role1} and ${suggestion.role2}`}`
        };
        
        try {
          const response = await appService.createSODRule(apiData);
          
          // Add the new rule to the UI
          const newRule = {
            id: response.id || new Date().getTime(),
            role1: apiData.ROLE1,
            role2: apiData.ROLE2,
            conflictType: apiData.CONFLICT_TYPE,
            description: apiData.DESCRIPTION,
            createdAt: new Date().toISOString().split('T')[0]
          };
          
          setSodRules(prevRules => [...prevRules, newRule]);
          successCount++;
        } catch (error) {
          console.error('Error creating rule from suggestion:', error);
          failureCount++;
        }
      }
      
      // Display results
      if (duplicateCount > 0) {
        setFileUploadError(
          `Successfully added ${successCount} rules. ` + 
          `Skipped ${duplicateCount} duplicate rules. ` + 
          (failureCount > 0 ? `Failed to add ${failureCount} rules.` : '')
        );
      } else if (failureCount > 0) {
        setFileUploadError(`Successfully added ${successCount} rules, failed to add ${failureCount} rules.`);
      } else {
        setUploadSuccess(true);
        setTimeout(() => {
          setUploadSuccess(false);
        }, 3000);
      }
      
      // Remove added suggestions from the list
      const remainingIndices = new Set(selectedSuggestions);
      const updatedSuggestions = aiSuggestions.filter((_, index) => !remainingIndices.has(index));
      setAiSuggestions(updatedSuggestions);
      
      // Clear selection
      setSelectedSuggestions([]);
      
    } catch (error) {
      console.error('Error adding multiple rules:', error);
      setFileUploadError('An error occurred while adding multiple rules.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Download roles data as Excel
  const handleDownloadRoles = () => {
    if (!roleData || roleData.roles.length === 0) return;
    
    // Create worksheet data with headers
    const worksheetData = [
      ['Role', 'Total Users']
    ];
    
    // Add role summary data
    roleData.roles.forEach(role => {
      worksheetData.push([
        role.role,
        role.count
      ]);
    });

    // Create a workbook with the worksheet
    const workbook = XLSX.utils.book_new();
    
    // Add role summary sheet
    const summaryWs = XLSX.utils.aoa_to_sheet(worksheetData);
    XLSX.utils.book_append_sheet(workbook, summaryWs, 'Roles Summary');
    
    // Generate file name with app name
    const appName = selectedApp ? 
      `${selectedApp.APP_NAME.replace(/\s+/g, '_')}_sod_rules_template.xlsx` : 
      'sod_rules_template.xlsx';
    
    // Download the file
    XLSX.writeFile(workbook, appName);
  };

  // Data Grid columns for SOD rules
  const columns = [
    { field: 'id', headerName: 'ID', width: 70, flex: 0 },
    { field: 'role1', headerName: 'Role 1', width: 180, flex: 1, minWidth: 150, renderCell: (params) => (
      <mui.Typography variant="body2" sx={{ fontWeight: 500 }}>{params.value}</mui.Typography>
    )},
    { field: 'role2', headerName: 'Role 2', width: 180, flex: 1, minWidth: 150, renderCell: (params) => (
      <mui.Typography variant="body2" sx={{ fontWeight: 500 }}>{params.value}</mui.Typography>
    )},
    { field: 'conflictType', headerName: 'Conflict Severity', width: 140, flex: 0, renderCell: (params) => {
      let color = 'primary.main';
      let bgColor = 'rgba(2, 136, 209, 0.1)';
      
      if (params.value === 'Critical') {
        color = '#d32f2f';
        bgColor = 'rgba(211, 47, 47, 0.1)';
      } else if (params.value === 'High') {
        color = '#e65100';
        bgColor = 'rgba(230, 81, 0, 0.1)';
      } else if (params.value === 'Medium') {
        color = '#ed6c02';
        bgColor = 'rgba(237, 108, 2, 0.1)';
      } else if (params.value === 'Low') {
        color = '#2e7d32';
        bgColor = 'rgba(46, 125, 50, 0.1)';
      }
      
      return (
        <mui.Chip 
          label={params.value} 
          size="small"
          sx={{ 
            color: color,
            bgcolor: bgColor,
            fontWeight: 500,
            borderRadius: '4px'
          }}
        />
      );
    }},
    { field: 'description', headerName: 'Description', width: 300, flex: 2, minWidth: 200 },
    { field: 'createdAt', headerName: 'Created Date', width: 120, flex: 0 },
    { field: 'actions', headerName: 'Actions', width: 100, flex: 0, sortable: false, renderCell: (params) => (
      <mui.Box>
        <mui.IconButton 
          size="small" 
          onClick={() => handleEditRule(params.row)}
          sx={{ color: 'primary.main' }}
        >
          <EditIcon fontSize="small" />
        </mui.IconButton>
        <mui.IconButton 
          size="small" 
          onClick={() => handleOpenDialog('deleteRule', params.row)}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize="small" />
        </mui.IconButton>
      </mui.Box>
    )}
  ];

  // Main content of the page
  const customMainContent = (
    <div>
      {initialLoading ? (
        <mui.Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          height: '100vh',
          width: '100%'
        }}>
          <LogoLoader 
            size={80} 
            message="Loading SOD Library..." 
            color="#0288d1"
            duration={1.5}
          />
        </mui.Box>
      ) : (
        <>
          <ResponsiveContainer>
            <mui.Box sx={{ padding: '24px' }}>
              {/* Breadcrumbs */}
              <mui.Breadcrumbs 
                aria-label="breadcrumb"
                sx={{
                  '& .MuiLink-root': {
                    display: 'flex',
                    alignItems: 'center',
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                    transition: 'color 0.2s ease',
                    '&:hover': {
                      color: 'primary.main'
                    }
                  }
                }}
              >
                <mui.Link underline="hover" color="inherit" href="/dashboard">
                  <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
                </mui.Link>
                <mui.Link underline="hover" color="inherit" href="/dashboard">
                  My Dashboard
                </mui.Link>
                <mui.Typography color="text.primary">Segregation of Duties Library</mui.Typography>
              </mui.Breadcrumbs>

              {/* Header */}
              <mui.Box sx={{ 
                mt: 3, 
                mb: 4, 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ShieldIcon 
                    sx={{ 
                      fontSize: 32, 
                      color: 'primary.main',
                      mr: 1,
                    }} 
                  />
                  <mui.Typography
                    variant="h4"
                    component="h1"
                    sx={{
                      fontWeight: 600,
                      color: 'text.primary',
                    }}
                  >
                    SOD Library
                  </mui.Typography>
                </mui.Box>
              </mui.Box>

              {/* Application Selection - Simplified Display */}
              <mui.Paper 
                elevation={0}
                sx={{ 
                  p: 3, 
                  mb: 4, 
                  borderRadius: 2,
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  boxShadow: 'rgba(0, 0, 0, 0.04) 0px 3px 12px',
                  background: 'linear-gradient(to right bottom, rgba(255, 255, 255, 1), rgba(246, 246, 246, 0.5))'
                }}
              >
                <mui.Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between'
                }}>
                  {selectedApp ? (
                    <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <mui.Avatar
                        sx={{
                          width: 48,
                          height: 48,
                          bgcolor: 'primary.main',
                          mr: 2
                        }}
                      >
                        {selectedApp.APP_NAME.substring(0, 1)}
                      </mui.Avatar>
                      <mui.Box>
                        <mui.Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {selectedApp.APP_NAME}
                        </mui.Typography>
                        {selectedApp.COMPANY_NAME && (
                          <mui.Typography variant="body2" color="text.secondary">
                            {selectedApp.COMPANY_NAME}
                          </mui.Typography>
                        )}
                      </mui.Box>
                    </mui.Box>
                  ) : (
                    <mui.Typography 
                      variant="h6" 
                      sx={{ 
                        color: 'text.primary',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <SettingsIcon sx={{ mr: 1, color: 'primary.main' }} />
                      Select an Application
                    </mui.Typography>
                  )}
                  
                  <mui.Button
                    variant={selectedApp ? "outlined" : "contained"}
                    color="primary"
                    onClick={handleOpenAppDialog}
                    startIcon={<SettingsIcon />}
                    sx={{ 
                      textTransform: 'none',
                      fontWeight: 500,
                      px: 2,
                      py: 1,
                      borderRadius: '10px'
                    }}
                  >
                    {selectedApp ? "Change Application" : "Select Application"}
                  </mui.Button>
                </mui.Box>
                
                {isLoading && (
                  <mui.Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                    <mui.CircularProgress size={32} />
                  </mui.Box>
                )}
                
                {!isLoading && applications.length === 0 && (
                  <mui.Alert severity="info" sx={{ mt: 2 }}>
                    No applications found. Please add applications to your system first.
                  </mui.Alert>
                )}
              </mui.Paper>
              
              {/* Application Selection Dialog */}
              <mui.Dialog 
                open={appDialogOpen} 
                onClose={() => setAppDialogOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                  sx: {
                    borderRadius: 3,
                    height: 'min(80vh, 700px)'
                  }
                }}
              >
                <mui.DialogTitle sx={{ 
                  px: 3, 
                  py: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
                }}>
                  <mui.Box>
                    <mui.Typography variant="h6" fontWeight={600}>
                      Select Application
                    </mui.Typography>
                    <mui.Typography variant="caption" color="text.secondary">
                      Showing applications with assigned roles only
                    </mui.Typography>
                  </mui.Box>
                  <mui.IconButton onClick={() => setAppDialogOpen(false)} size="small">
                    <mui.Icon>close</mui.Icon>
                  </mui.IconButton>
                </mui.DialogTitle>
                
                <mui.DialogContent sx={{ p: 0 }}>
                  <mui.Box sx={{ p: 3, borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
                    <mui.Grid container spacing={2}>
                      <mui.Grid item xs={12} md={8}>
                        <mui.TextField 
                          fullWidth
                          label="Search Applications"
                          placeholder="Type to search by name..."
                          variant="outlined"
                          value={appSearchTerm}
                          onChange={(e) => setAppSearchTerm(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <mui.InputAdornment position="start">
                                <mui.Icon>search</mui.Icon>
                              </mui.InputAdornment>
                            ),
                            sx: { borderRadius: 2 }
                          }}
                        />
                      </mui.Grid>
                      <mui.Grid item xs={12} md={4}>
                        <mui.FormControl fullWidth variant="outlined">
                          <mui.InputLabel id="company-filter-label">Filter by Company</mui.InputLabel>
                          <mui.Select
                            labelId="company-filter-label"
                            value={appFilterCompany}
                            onChange={(e) => setAppFilterCompany(e.target.value)}
                            label="Filter by Company"
                            sx={{ borderRadius: 2 }}
                          >
                            <mui.MenuItem value="">
                              <em>All Companies</em>
                            </mui.MenuItem>
                            {companyNames.map((company) => (
                              <mui.MenuItem key={company} value={company}>
                                {company}
                              </mui.MenuItem>
                            ))}
                          </mui.Select>
                        </mui.FormControl>
                      </mui.Grid>
                    </mui.Grid>
                  </mui.Box>
                  
                  <mui.Box sx={{ height: 'calc(80vh - 200px)', overflow: 'auto' }}>
                    {isLoading ? (
                      <mui.Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <mui.CircularProgress />
                      </mui.Box>
                    ) : filteredApps.length > 0 ? (
                      <mui.List sx={{ p: 0 }}>
                        {(() => {
                          // Group applications by company
                          const companies = {};
                          const ungrouped = [];
                          
                          filteredApps.forEach(app => {
                            if (app.COMPANY_NAME) {
                              if (!companies[app.COMPANY_NAME]) {
                                companies[app.COMPANY_NAME] = [];
                              }
                              companies[app.COMPANY_NAME].push(app);
                            } else {
                              ungrouped.push(app);
                            }
                          });
                          
                          const listItems = [];
                          
                          // Add company groups
                          Object.keys(companies).sort().forEach(company => {
                            if (!appFilterCompany || company === appFilterCompany) {
                              listItems.push(
                                <mui.ListSubheader 
                                  key={`company-${company}`} 
                                  sx={{ 
                                    bgcolor: 'rgba(0, 0, 0, 0.02)', 
                                    lineHeight: '40px',
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 10,
                                    borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
                                  }}
                                >
                                  {company} ({companies[company].length})
                                </mui.ListSubheader>
                              );
                              
                              companies[company]
                                .sort((a, b) => a.APP_NAME.localeCompare(b.APP_NAME))
                                .forEach(app => {
                                  listItems.push(
                                    <mui.ListItem 
                                      key={app.id} 
                                      button 
                                      onClick={() => handleSelectApp(app)}
                                      sx={{ 
                                        py: 1.5,
                                        borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                          bgcolor: 'rgba(2, 136, 209, 0.08)'
                                        }
                                      }}
                                    >
                                      <mui.ListItemAvatar>
                                        <mui.Avatar
                                          sx={{
                                            bgcolor: `rgba(2, 136, 209, ${Math.random() * 0.5 + 0.3})`
                                          }}
                                        >
                                          {app.APP_NAME.substring(0, 1)}
                                        </mui.Avatar>
                                      </mui.ListItemAvatar>
                                      <mui.ListItemText 
                                        primary={
                                          <mui.Typography variant="body1" fontWeight={500}>
                                            {app.APP_NAME}
                                          </mui.Typography>
                                        } 
                                        secondary={
                                          <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <mui.Typography variant="body2" color="text.secondary">
                                              {company}
                                            </mui.Typography>
                                            <mui.Box 
                                              component="span" 
                                              sx={{ 
                                                display: 'inline-block', 
                                                width: 4, 
                                                height: 4, 
                                                borderRadius: '50%',
                                                bgcolor: 'text.disabled',
                                                mx: 1
                                              }} 
                                            />
                                            <mui.Typography variant="caption" color="text.secondary">
                                              ID: {app.id.substring(0, 8)}
                                            </mui.Typography>
                                          </mui.Box>
                                        }
                                      />
                                      <mui.ListItemSecondaryAction>
                                        <mui.IconButton edge="end" size="small" onClick={() => handleSelectApp(app)}>
                                          <mui.Icon>arrow_forward</mui.Icon>
                                        </mui.IconButton>
                                      </mui.ListItemSecondaryAction>
                                    </mui.ListItem>
                                  );
                                });
                            }
                          });
                          
                          // Add ungrouped apps
                          if (ungrouped.length > 0 && !appFilterCompany) {
                            listItems.push(
                              <mui.ListSubheader 
                                key="ungrouped" 
                                sx={{ 
                                  bgcolor: 'rgba(0, 0, 0, 0.02)', 
                                  lineHeight: '40px',
                                  position: 'sticky',
                                  top: 0,
                                  zIndex: 10,
                                  borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
                                }}
                              >
                                Other Applications ({ungrouped.length})
                              </mui.ListSubheader>
                            );
                            
                            ungrouped
                              .sort((a, b) => a.APP_NAME.localeCompare(b.APP_NAME))
                              .forEach(app => {
                                listItems.push(
                                  <mui.ListItem 
                                    key={app.id} 
                                    button 
                                    onClick={() => handleSelectApp(app)}
                                    sx={{ 
                                      py: 1.5,
                                      borderBottom: '1px solid rgba(0, 0, 0, 0.04)',
                                      transition: 'all 0.2s',
                                      '&:hover': {
                                        bgcolor: 'rgba(2, 136, 209, 0.08)'
                                      }
                                    }}
                                  >
                                    <mui.ListItemAvatar>
                                      <mui.Avatar
                                        sx={{
                                          bgcolor: `rgba(2, 136, 209, ${Math.random() * 0.5 + 0.3})`
                                        }}
                                      >
                                        {app.APP_NAME.substring(0, 1)}
                                      </mui.Avatar>
                                    </mui.ListItemAvatar>
                                    <mui.ListItemText 
                                      primary={
                                        <mui.Typography variant="body1" fontWeight={500}>
                                          {app.APP_NAME}
                                        </mui.Typography>
                                      } 
                                      secondary={
                                        <mui.Typography variant="caption" color="text.secondary">
                                          ID: {app.id.substring(0, 8)}
                                        </mui.Typography>
                                      }
                                    />
                                    <mui.ListItemSecondaryAction>
                                      <mui.IconButton edge="end" size="small" onClick={() => handleSelectApp(app)}>
                                        <mui.Icon>arrow_forward</mui.Icon>
                                      </mui.IconButton>
                                    </mui.ListItemSecondaryAction>
                                  </mui.ListItem>
                                );
                              });
                          }
                          
                          return listItems;
                        })()}
                      </mui.List>
                    ) : (
                      <mui.Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        p: 4,
                        height: '100%'
                      }}>
                        <mui.Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                          No results found
                        </mui.Typography>
                        <mui.Typography variant="body2" color="text.secondary">
                          Try adjusting your search or filters
                        </mui.Typography>
                      </mui.Box>
                    )}
                  </mui.Box>
                </mui.DialogContent>
                
                <mui.DialogActions sx={{ borderTop: '1px solid rgba(0, 0, 0, 0.08)', p: 2 }}>
                  <mui.Button 
                    onClick={() => setAppDialogOpen(false)}
                    color="inherit"
                    sx={{ textTransform: 'none' }}
                  >
                    Cancel
                  </mui.Button>
                  <mui.Button 
                    onClick={() => {
                      setAppDialogOpen(false);
                      setAppSearchTerm('');
                      setAppFilterCompany('');
                    }}
                    variant="contained"
                    color="primary"
                    sx={{ textTransform: 'none', ml: 1 }}
                    disabled={filteredApps.length === 0}
                  >
                    Close
                  </mui.Button>
                </mui.DialogActions>
              </mui.Dialog>

              {/* Application Selection and Top Roles - Combined in a single row */}
              {selectedApp && (
                <mui.Grid container spacing={3} sx={{ mb: 4 }}>
                  {/* Application Roles - Minimalist Card */}
                  <mui.Grid item xs={12} md={4}>
                    <mui.Paper 
                      elevation={0}
                      sx={{ 
                        p: 2, 
                        height: '100%',
                        borderRadius: 2,
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <mui.Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        mb: 1
                      }}>
                        <mui.Typography 
                          variant="subtitle1" 
                          sx={{ 
                            color: 'text.primary',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: '0.9rem'
                          }}
                        >
                          <BusinessCenterIcon sx={{ mr: 0.75, color: 'primary.main', fontSize: '1rem' }} />
                          Top Roles
                        </mui.Typography>
                        
                        <mui.Tooltip title="Download All Roles">
                          <mui.IconButton
                            size="small"
                            color="primary"
                            onClick={handleDownloadRoles}
                            disabled={roleData.roles.length === 0 || rolesLoading}
                          >
                            <CloudDownloadIcon fontSize="small" />
                          </mui.IconButton>
                        </mui.Tooltip>
                      </mui.Box>
                      
                      {rolesLoading ? (
                        <mui.Box sx={{ display: 'flex', justifyContent: 'center', p: 2, flexGrow: 1, alignItems: 'center' }}>
                          <mui.CircularProgress size={24} />
                        </mui.Box>
                      ) : roleData.roles.length > 0 ? (
                        <mui.TableContainer 
                          sx={{ 
                            maxHeight: 160,
                            flexGrow: 1,
                            '&::-webkit-scrollbar': {
                              width: '4px',
                              height: '4px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              backgroundColor: 'rgba(0,0,0,0.1)',
                              borderRadius: '4px',
                            },
                          }}
                        >
                          <mui.Table size="small" stickyHeader>
                            <mui.TableHead>
                              <mui.TableRow>
                                <mui.TableCell sx={{ py: 0.75, fontSize: '0.75rem', fontWeight: 600 }}>Role Name</mui.TableCell>
                                <mui.TableCell align="right" sx={{ py: 0.75, fontSize: '0.75rem', fontWeight: 600 }}>Users</mui.TableCell>
                              </mui.TableRow>
                            </mui.TableHead>
                            <mui.TableBody>
                              {roleData.roles.slice(0, 8).map((role, index) => (
                                <mui.TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                  <mui.TableCell component="th" scope="row" sx={{ py: 0.5, fontSize: '0.75rem' }}>
                                    {role.role}
                                  </mui.TableCell>
                                  <mui.TableCell align="right" sx={{ py: 0.5 }}>
                                    <mui.Chip 
                                      label={role.count} 
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                      sx={{ minWidth: '32px', height: '18px', fontSize: '0.65rem' }}
                                    />
                                  </mui.TableCell>
                                </mui.TableRow>
                              ))}
                            </mui.TableBody>
                          </mui.Table>
                        </mui.TableContainer>
                      ) : (
                        <mui.Box sx={{ p: 1, flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <mui.Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
                            No roles available
                          </mui.Typography>
                        </mui.Box>
                      )}
                    </mui.Paper>
                  </mui.Grid>
                  
                  {/* SOX AI Analysis Card */}
                  <mui.Grid item xs={12} md={8}>
                    <mui.Paper 
                      elevation={0}
                      sx={{ 
                        p: 2, 
                        height: '100%',
                        borderRadius: 2,
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        background: 'linear-gradient(to right, rgba(2, 136, 209, 0.02), rgba(46, 125, 50, 0.02))'
                      }}
                    >
                      <mui.Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        mb: 1
                      }}>
                        <mui.Typography 
                          variant="subtitle1" 
                          sx={{ 
                            color: 'text.primary',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <AutoFixHighIcon sx={{ mr: 0.75, color: 'primary.main' }} />
                          AI-Driven SOX Analysis
                        </mui.Typography>
                        
                        <mui.Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={isAnalyzing ? <mui.CircularProgress size={16} color="inherit" /> : <AutoFixHighIcon />}
                          onClick={getAISuggestedRules}
                          disabled={isAnalyzing || rolesLoading || roleData.roles.length === 0}
                          sx={{ 
                            textTransform: 'none',
                            fontWeight: 500,
                            px: 2,
                            borderRadius: '8px',
                            boxShadow: 'none'
                          }}
                        >
                          {isAnalyzing ? 'Analyzing...' : roleData.roles.length === 0 ? 'No Roles Available' : 'Analyze SOX Conflicts'}
                        </mui.Button>
                      </mui.Box>
                      
                      <mui.Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.75rem' }}>
                        AI analyzes your app roles to identify SOX compliance conflicts, focusing on financial controls and excluding operational issues.
                      </mui.Typography>

                      {aiSuggestions.length > 0 && (
                        <mui.Button
                          variant="outlined" 
                          color="primary"
                          fullWidth
                          size="small"
                          onClick={() => document.getElementById('ai-suggestions-section').scrollIntoView({ behavior: 'smooth' })}
                          sx={{ 
                            textTransform: 'none',
                            fontWeight: 500,
                            mt: 0.5,
                            borderRadius: '8px',
                            borderStyle: 'dashed'
                          }}
                        >
                          View {aiSuggestions.length} SOX Rule Suggestions
                        </mui.Button>
                      )}
                    </mui.Paper>
                  </mui.Grid>
                </mui.Grid>
              )}

              {/* SOD Rules Management - Emphasized */}
              {selectedApp && (
                <mui.Paper 
                  elevation={0}
                  sx={{ 
                    p: 0, 
                    mb: 4, 
                    borderRadius: 2,
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    boxShadow: 'rgba(0, 0, 0, 0.05) 0px 6px 24px 0px, rgba(0, 0, 0, 0.08) 0px 0px 0px 1px',
                    overflow: 'hidden'
                  }}
                >
                  <mui.Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    p: 2,
                    borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                    bgcolor: 'rgba(2, 136, 209, 0.03)'
                  }}>
                    <mui.Typography 
                      variant="h6" 
                      sx={{ 
                        color: 'text.primary',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '1.1rem'
                      }}
                    >
                      <ShieldIcon sx={{ mr: 1, color: 'primary.main' }} />
                      SOD Rules for {selectedApp.APP_NAME}
                    </mui.Typography>
                    
                    <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <mui.Button
                        variant="contained"
                        color="primary"
                        disableElevation
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={handleAddRule}
                        sx={{ 
                          textTransform: 'none',
                          fontWeight: 500,
                          height: 36,
                          borderRadius: '8px',
                          boxShadow: 'none',
                          mr: 2
                        }}
                      >
                        Add Rule
                      </mui.Button>
                      
                      <mui.ButtonGroup 
                        variant="outlined" 
                        size="medium"
                        sx={{ height: 36 }}
                      >
                        <mui.Button
                          component="label"
                          color="primary"
                          startIcon={isUploading ? <mui.CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
                          disabled={isUploading}
                          sx={{ 
                            textTransform: 'none',
                            borderTopLeftRadius: '8px',
                            borderBottomLeftRadius: '8px',
                            borderColor: 'rgba(0, 0, 0, 0.12)',
                            height: 36
                          }}
                        >
                          {isUploading ? 'Uploading...' : 'Upload'}
                          <VisuallyHiddenInput 
                            type="file" 
                            onChange={handleFileUpload}
                            accept=".xlsx,.xls,.csv"
                            ref={fileInputRef}
                            disabled={isUploading}
                          />
                        </mui.Button>
                        
                        <mui.Button
                          color="primary"
                          startIcon={<CloudDownloadIcon />}
                          onClick={handleDownloadTemplate}
                          disabled={isUploading}
                          sx={{ 
                            textTransform: 'none',
                            borderTopRightRadius: '8px',
                            borderBottomRightRadius: '8px',
                            borderColor: 'rgba(0, 0, 0, 0.12)',
                            height: 36
                          }}
                        >
                          Template
                        </mui.Button>
                      </mui.ButtonGroup>
                    </mui.Box>
                  </mui.Box>

                  {/* Feedback messages */}
                  {fileUploadError && (
                    <mui.Alert severity="error" sx={{ m: 2, mb: 0 }}>
                      {fileUploadError}
                    </mui.Alert>
                  )}
                  
                  {uploadSuccess && (
                    <mui.Alert severity="success" sx={{ m: 2, mb: 0 }}>
                      Rules uploaded successfully
                    </mui.Alert>
                  )}
                  
                  {/* SOD Rules Data Grid */}
                  <mui.Box sx={{ height: 400, width: '100%', p: 0, overflow: 'hidden' }}>
                    <DataGrid
                      rows={sodRules}
                      columns={columns}
                      pageSize={5}
                      rowsPerPageOptions={[5, 10, 25]}
                      checkboxSelection
                      disableSelectionOnClick
                      components={{ Toolbar: GridToolbar }}
                      componentsProps={{
                        toolbar: {
                          showQuickFilter: true,
                          quickFilterProps: { debounceMs: 500 },
                        },
                      }}
                      autoWidth={true}
                      sx={{
                        '& .MuiDataGrid-toolbarContainer': {
                          borderBottom: '1px solid rgba(224, 224, 224, 1)',
                          padding: '8px 16px',
                          backgroundColor: 'rgba(0, 0, 0, 0.01)'
                        },
                        border: 'none',
                        '& .MuiDataGrid-cell:focus': {
                          outline: 'none',
                        },
                        '& .MuiDataGrid-cell': {
                          borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                        },
                        '& .MuiDataGrid-main': {
                          overflow: 'hidden',
                        },
                        '& .MuiDataGrid-columnHeaders': {
                          backgroundColor: 'rgba(0, 0, 0, 0.02)',
                        },
                      }}
                    />
                  </mui.Box>
                </mui.Paper>
              )}
              
              {/* AI-Generated SOD Rule Suggestions - Now a dedicated section with premium look */}
              {selectedApp && aiSuggestions.length > 0 && (
                <mui.Paper 
                  id="ai-suggestions-section"
                  elevation={0}
                  sx={{ 
                    mb: 4, 
                    borderRadius: 2,
                    border: '1px solid rgba(2, 136, 209, 0.2)',
                    overflow: 'hidden',
                    background: 'linear-gradient(to right, rgba(2, 136, 209, 0.02), rgba(46, 125, 50, 0.02))'
                  }}
                >
                  <mui.Box sx={{ 
                    bgcolor: 'rgba(2, 136, 209, 0.05)',
                    p: 2,
                    borderBottom: '1px solid rgba(2, 136, 209, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <mui.Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                        <AutoFixHighIcon />
                      </mui.Avatar>
                      <mui.Box>
                        <mui.Typography variant="h6" fontWeight={600}>
                          AI-Suggested SOX Compliance Rules
                        </mui.Typography>
                        <mui.Typography variant="body2" color="text.secondary">
                          {aiSuggestions.filter(s => s.conflictCount > 0).length} potential conflicts detected across {aiSuggestions.length} rule suggestions
                        </mui.Typography>
                      </mui.Box>
                    </mui.Box>
                    
                    <mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {selectedSuggestions.length > 0 && (
                        <mui.Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={handleAddMultipleSuggestedRules}
                          disabled={selectedSuggestions.length === 0}
                          startIcon={<AddCircleOutlineIcon />}
                          sx={{ 
                            mr: 1,
                            textTransform: 'none',
                            fontWeight: 500
                          }}
                        >
                          Add {selectedSuggestions.length} Selected Rule{selectedSuggestions.length > 1 ? 's' : ''}
                        </mui.Button>
                      )}
                      <mui.Chip 
                        label="AI Powered" 
                        color="primary"
                        icon={<AutoFixHighIcon />}
                        sx={{ fontWeight: 500, borderRadius: '16px' }}
                      />
                    </mui.Box>
                  </mui.Box>
                  
                  <mui.TableContainer sx={{ 
                    maxHeight: 350,
                    width: '100%',
                    '&::-webkit-scrollbar': {
                      width: '8px',
                      height: '8px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'rgba(0,0,0,0.1)',
                      borderRadius: '4px',
                    }
                  }}>
                    <mui.Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
                      <mui.TableHead>
                        <mui.TableRow>
                          <mui.TableCell padding="checkbox" sx={{ width: '48px' }}>
                            <mui.Checkbox
                              indeterminate={selectedSuggestions.length > 0 && selectedSuggestions.length < aiSuggestions.length}
                              checked={selectedSuggestions.length > 0 && selectedSuggestions.length === aiSuggestions.length}
                              onChange={handleSelectAllSuggestions}
                              size="small"
                            />
                          </mui.TableCell>
                          <mui.TableCell sx={{ fontWeight: 600, width: '15%' }}>Role 1</mui.TableCell>
                          <mui.TableCell sx={{ fontWeight: 600, width: '15%' }}>Role 2</mui.TableCell>
                          <mui.TableCell sx={{ fontWeight: 600, width: '10%' }}>Severity</mui.TableCell>
                          <mui.TableCell sx={{ fontWeight: 600, width: '15%' }}>SOX Category</mui.TableCell>
                          <mui.TableCell sx={{ fontWeight: 600, width: '10%' }}>Conflicts</mui.TableCell>
                          <mui.TableCell sx={{ fontWeight: 600, width: '20%' }}>Explanation</mui.TableCell>
                          <mui.TableCell align="right" sx={{ fontWeight: 600, width: '15%' }}>Action</mui.TableCell>
                        </mui.TableRow>
                      </mui.TableHead>
                      <mui.TableBody>
                        {aiSuggestions.map((suggestion, index) => (
                          <mui.TableRow key={index} sx={{
                            backgroundColor: suggestion.conflictCount > 0 ? 'rgba(211, 47, 47, 0.04)' : 'inherit',
                            '&:hover': {
                              backgroundColor: suggestion.conflictCount > 0 ? 'rgba(211, 47, 47, 0.08)' : 'rgba(0, 0, 0, 0.04)'
                            },
                            transition: 'background-color 0.2s'
                          }}>
                            <mui.TableCell padding="checkbox">
                              <mui.Checkbox
                                checked={selectedSuggestions.includes(index)}
                                onChange={() => handleSelectSuggestion(index)}
                                size="small"
                              />
                            </mui.TableCell>
                            <mui.TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <mui.Tooltip title={suggestion.role1} arrow>
                                <mui.Typography variant="body2">{suggestion.role1}</mui.Typography>
                              </mui.Tooltip>
                            </mui.TableCell>
                            <mui.TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <mui.Tooltip title={suggestion.role2} arrow>
                                <mui.Typography variant="body2">{suggestion.role2}</mui.Typography>
                              </mui.Tooltip>
                            </mui.TableCell>
                            <mui.TableCell>
                              <mui.Chip 
                                label={suggestion.severity} 
                                size="small"
                                color={
                                  suggestion.severity === 'Critical' ? 'error' :
                                  suggestion.severity === 'High' ? 'warning' :
                                  suggestion.severity === 'Medium' ? 'primary' : 'success'
                                }
                                sx={{ 
                                  fontWeight: 500,
                                  borderRadius: '4px'
                                }}
                              />
                            </mui.TableCell>
                            <mui.TableCell>
                              <mui.Chip 
                                label={suggestion.soxCategory || 'Financial Reporting'} 
                                size="small"
                                color="info"
                                variant="outlined"
                                sx={{ 
                                  fontWeight: 500,
                                  borderRadius: '4px',
                                  fontSize: '0.7rem'
                                }}
                              />
                            </mui.TableCell>
                            <mui.TableCell>
                              {suggestion.conflictCount > 0 ? (
                                <mui.Chip 
                                  label={suggestion.conflictCount} 
                                  size="small"
                                  color="error"
                                  sx={{ 
                                    fontWeight: 600,
                                    borderRadius: '4px'
                                  }}
                                />
                              ) : (
                                <mui.Typography variant="body2" color="text.secondary">
                                  None
                                </mui.Typography>
                              )}
                            </mui.TableCell>
                            <mui.TableCell sx={{ maxWidth: 200 }}>
                              <mui.Tooltip title={suggestion.explanation || ''} arrow>
                                <mui.Typography 
                                  variant="body2" 
                                  sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    fontSize: '0.75rem'
                                  }}
                                >
                                  {suggestion.explanation || 'No explanation provided'}
                                </mui.Typography>
                              </mui.Tooltip>
                            </mui.TableCell>
                            <mui.TableCell align="right">
                              <mui.Button 
                                variant={suggestion.conflictCount > 0 ? "contained" : "outlined"}
                                color={suggestion.conflictCount > 0 ? "error" : "primary"}
                                size="small"
                                onClick={() => handleAddSuggestedRule(suggestion)}
                                sx={{ borderRadius: '4px', textTransform: 'none' }}
                              >
                                {suggestion.conflictCount > 0 ? "Add Critical Rule" : "Add Rule"}
                              </mui.Button>
                            </mui.TableCell>
                          </mui.TableRow>
                        ))}
                      </mui.TableBody>
                    </mui.Table>
                  </mui.TableContainer>
                  
                  <mui.Box sx={{ p: 2, borderTop: '1px solid rgba(2, 136, 209, 0.1)', display: 'flex', justifyContent: 'space-between' }}>
                    <mui.Box>
                      {selectedSuggestions.length > 0 && (
                        <mui.Button
                          variant="contained"
                          color="primary"
                          onClick={handleAddMultipleSuggestedRules}
                          disabled={selectedSuggestions.length === 0}
                          startIcon={<AddCircleOutlineIcon />}
                          sx={{ 
                            textTransform: 'none',
                            fontWeight: 500,
                            mr: 2
                          }}
                        >
                          Add {selectedSuggestions.length} Selected Rule{selectedSuggestions.length > 1 ? 's' : ''}
                        </mui.Button>
                      )}
                      <mui.Button
                        variant="outlined"
                        color="primary"
                        onClick={handleSelectAllSuggestions}
                        sx={{ 
                          textTransform: 'none',
                          fontWeight: 500
                        }}
                      >
                        {selectedSuggestions.length === aiSuggestions.length 
                          ? "Deselect All" 
                          : "Select All"}
                      </mui.Button>
                    </mui.Box>
                    
                    <mui.Button
                      variant="text"
                      onClick={getAISuggestedRules}
                      startIcon={<AutoFixHighIcon />}
                      sx={{ textTransform: 'none', fontWeight: 500 }}
                    >
                      Refresh AI Analysis
                    </mui.Button>
                  </mui.Box>
                </mui.Paper>
              )}
              
              {/* AI Suggestions - Loading and Empty States */}
              {selectedApp && isAnalyzing && (
                <mui.Paper
                  elevation={0}
                  sx={{ 
                    p: 4, 
                    mb: 4, 
                    borderRadius: 2,
                    border: '1px dashed rgba(0, 0, 0, 0.12)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    background: 'linear-gradient(to right, rgba(2, 136, 209, 0.02), rgba(46, 125, 50, 0.02))'
                  }}
                >
                  <mui.CircularProgress size={40} sx={{ mb: 2 }} />
                  <mui.Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                    Analyzing Roles with AI
                  </mui.Typography>
                  <mui.Typography variant="body2" color="text.secondary">
                    Our AI is analyzing {roleData.roles.length} roles across {roleData.userRolePairs.size} users to identify SOX compliance conflicts
                  </mui.Typography>
                </mui.Paper>
              )}
              
              {analysisError && (
                <mui.Alert severity="error" sx={{ mb: 4 }}>
                  {analysisError}
                </mui.Alert>
              )}

              {/* SOD Insights - New Compact Features Section */}
              <mui.Box sx={{ mb: 4 }}>
                <mui.Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 2, 
                    fontWeight: 600,
                    color: 'text.primary',
                    display: 'flex', 
                    alignItems: 'center'
                  }}
                >
                  <mui.Box component="span" 
                    sx={{ 
                      width: 24, 
                      height: 3, 
                      bgcolor: 'primary.main', 
                      display: 'inline-block', 
                      mr: 1, 
                      borderRadius: 1 
                    }} 
                  />
                  SOD Compliance Insights
                </mui.Typography>

                <mui.Grid container spacing={2}>
                  <mui.Grid item xs={12} md={4}>
                    <mui.Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        height: '100%',
                        borderRadius: 2,
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 10px 20px rgba(0, 0, 0, 0.05)',
                        }
                      }}
                    >
                      <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <mui.Avatar
                          sx={{
                            bgcolor: 'rgba(2, 136, 209, 0.1)',
                            color: 'primary.main',
                            width: 40,
                            height: 40,
                            mr: 1.5,
                          }}
                        >
                          <SecurityIcon fontSize="small" />
                        </mui.Avatar>
                        <mui.Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Conflict Detection
                        </mui.Typography>
                      </mui.Box>
                      <mui.Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, fontSize: '0.875rem' }}>
                        AI-powered identification of SOX compliance violations and potential financial reporting conflicts.
                      </mui.Typography>
                    </mui.Paper>
                  </mui.Grid>
                  
                  <mui.Grid item xs={12} md={4}>
                    <mui.Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        height: '100%',
                        borderRadius: 2,
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 10px 20px rgba(0, 0, 0, 0.05)',
                        }
                      }}
                    >
                      <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <mui.Avatar
                          sx={{
                            bgcolor: 'rgba(76, 175, 80, 0.1)',
                            color: 'secondary.main',
                            width: 40,
                            height: 40,
                            mr: 1.5,
                          }}
                        >
                          <BusinessCenterIcon fontSize="small" />
                        </mui.Avatar>
                        <mui.Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Role Management
                        </mui.Typography>
                      </mui.Box>
                      <mui.Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, fontSize: '0.875rem' }}>
                        Define and manage financial roles with built-in controls to prevent unauthorized combinations.
                      </mui.Typography>
                    </mui.Paper>
                  </mui.Grid>
                  
                  <mui.Grid item xs={12} md={4}>
                    <mui.Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        height: '100%',
                        borderRadius: 2,
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 10px 20px rgba(0, 0, 0, 0.05)',
                        }
                      }}
                    >
                      <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <mui.Avatar
                          sx={{
                            bgcolor: 'rgba(2, 136, 209, 0.1)',
                            color: 'primary.main',
                            width: 40,
                            height: 40,
                            mr: 1.5,
                          }}
                        >
                          <VerifiedUserIcon fontSize="small" />
                        </mui.Avatar>
                        <mui.Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          SOX Compliance
                        </mui.Typography>
                      </mui.Box>
                      <mui.Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, fontSize: '0.875rem' }}>
                        Generate comprehensive SOX compliance reports with detailed remediation recommendations.
                      </mui.Typography>
                    </mui.Paper>
                  </mui.Grid>
                </mui.Grid>
              </mui.Box>
            </mui.Box>
          </ResponsiveContainer>
          
          {/* Dialogs */}
          <mui.Dialog
            open={openDialog}
            onClose={handleCloseDialog}
            PaperProps={{
              sx: {
                borderRadius: 2,
                maxWidth: dialogType === 'deleteRule' ? '500px' : '600px',
                width: '100%',
              }
            }}
          >
            {/* Delete Rule Confirmation Dialog */}
            {dialogType === 'deleteRule' && dialogData && (
              <>
                <mui.DialogTitle
                  sx={{
                    py: 3,
                    px: 3,
                    fontWeight: 600,
                    borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                    color: 'error.main',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <DeleteIcon sx={{ mr: 1 }} />
                  Confirm Deletion
                </mui.DialogTitle>
                <mui.DialogContent sx={{ p: 3, pt: 3 }}>
                  <mui.Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    Are you sure you want to delete this SOD rule?
                  </mui.Typography>
                  <mui.Box sx={{ 
                    p: 2, 
                    bgcolor: 'rgba(0, 0, 0, 0.02)', 
                    borderRadius: 1, 
                    border: '1px solid rgba(0, 0, 0, 0.08)' 
                  }}>
                    <mui.Typography variant="subtitle2" gutterBottom>
                      {dialogData.role1} ↔ {dialogData.role2}
                    </mui.Typography>
                    <mui.Typography variant="body2" color="text.secondary">
                      {dialogData.description}
                    </mui.Typography>
                  </mui.Box>
                </mui.DialogContent>
                <mui.DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid rgba(0, 0, 0, 0.08)' }}>
                  <mui.Button
                    onClick={handleCloseDialog}
                    sx={{
                      color: 'text.secondary',
                      textTransform: 'none',
                    }}
                  >
                    Cancel
                  </mui.Button>
                  <mui.Button
                    onClick={handleDeleteRule}
                    variant="contained"
                    color="error"
                    disableElevation
                    startIcon={<DeleteIcon />}
                    sx={{
                      px: 3,
                      borderRadius: 2,
                      textTransform: 'none',
                    }}
                  >
                    Delete Rule
                  </mui.Button>
                </mui.DialogActions>
              </>
            )}
            
            {/* Early Access Request Dialog (Legacy) */}
            {dialogType === 'earlyAccess' && (
              submitted ? (
                <mui.Box sx={{ p: 4, textAlign: 'center' }}>
                  <mui.Avatar
                    sx={{
                      backgroundColor: 'rgba(76, 175, 80, 0.1)',
                      color: 'secondary.main',
                      width: 80,
                      height: 80,
                      margin: '0 auto 24px auto',
                    }}
                  >
                    <ShieldIcon sx={{ fontSize: 40 }} />
                  </mui.Avatar>
                  <mui.Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                    Thank You!
                  </mui.Typography>
                  <mui.Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Your early access request has been received. We'll notify you as soon as the SOD Library becomes available.
                  </mui.Typography>
                  <mui.Button
                    variant="contained"
                    color="primary"
                    onClick={handleCloseDialog}
                    disableElevation
                    sx={{
                      px: 4,
                      py: 1,
                      borderRadius: 2,
                      textTransform: 'none',
                    }}
                  >
                    Close
                  </mui.Button>
                </mui.Box>
              ) : (
                <>
                  <mui.DialogTitle
                    sx={{
                      py: 3,
                      px: 3,
                      fontWeight: 600,
                      borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                    }}
                  >
                    Request Early Access to SOD Library
                  </mui.DialogTitle>
                  <mui.DialogContent sx={{ p: 3, pt: 3 }}>
                    <mui.Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Be among the first to experience our Segregation of Duties Library. Fill in your details below, and we'll notify you when it's ready.
                    </mui.Typography>
                    <mui.TextField
                      label="Name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      fullWidth
                      margin="normal"
                      variant="outlined"
                      sx={{ mb: 2 }}
                    />
                    <mui.TextField
                      label="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      fullWidth
                      required
                      margin="normal"
                      variant="outlined"
                    />
                  </mui.DialogContent>
                  <mui.DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid rgba(0, 0, 0, 0.08)' }}>
                    <mui.Button
                      onClick={handleCloseDialog}
                      sx={{
                        color: 'text.secondary',
                        textTransform: 'none',
                      }}
                    >
                      Cancel
                    </mui.Button>
                    <mui.Button
                      onClick={handleSubmit}
                      variant="contained"
                      disableElevation
                      sx={{
                        px: 3,
                        borderRadius: 2,
                        textTransform: 'none',
                      }}
                    >
                      Submit Request
                    </mui.Button>
                  </mui.DialogActions>
                </>
              )
            )}
          </mui.Dialog>
          
          {/* Rule Form Dialog - Moved outside of main Dialog */}
          <mui.Dialog
            open={showRuleForm}
            onClose={() => {
              setShowRuleForm(false);
              setCurrentRule(null);
            }}
            PaperProps={{
              sx: {
                borderRadius: 2,
                maxWidth: '600px',
                width: '100%',
              }
            }}
          >
            {currentRule && (
              <>
                <mui.DialogTitle
                  sx={{
                    py: 3,
                    px: 3,
                    fontWeight: 600,
                    borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {isEditMode ? (
                    <>
                      <EditIcon sx={{ mr: 1, color: 'primary.main' }} />
                      Edit SOD Rule
                    </>
                  ) : (
                    <>
                      <AddCircleOutlineIcon sx={{ mr: 1, color: 'primary.main' }} />
                      Add New SOD Rule
                    </>
                  )}
                </mui.DialogTitle>
                <mui.DialogContent sx={{ p: 3, pt: 3 }}>
                  <mui.Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {isEditMode 
                      ? `Update the details for this SOD rule for ${selectedApp?.APP_NAME || 'the selected application'}.`
                      : `Define a new SOD rule for ${selectedApp?.APP_NAME || 'the selected application'}.`
                    }
                  </mui.Typography>
                  
                  <Autocomplete
                    id="role1-autocomplete"
                    freeSolo
                    options={roleData.roles.map(role => role.role)}
                    value={currentRule.role1}
                    onChange={(event, newValue) => {
                      setCurrentRule({...currentRule, role1: newValue || ''});
                    }}
                    renderInput={(params) => (
                      <mui.TextField
                        {...params}
                        label="Role 1"
                        required
                        margin="normal"
                        variant="outlined"
                        fullWidth
                        error={!currentRule.role1}
                        helperText={!currentRule.role1 ? "Role 1 is required" : ""}
                        sx={{ mb: 2 }}
                      />
                    )}
                    renderOption={(props, option) => {
                      // Find the role data to show user count
                      const roleInfo = roleData.roles.find(r => r.role === option);
                      return (
                        <li {...props}>
                          <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                            <mui.Typography variant="body2">{option}</mui.Typography>
                            {roleInfo && (
                              <mui.Chip 
                                label={`${roleInfo.count} users`} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                                sx={{ ml: 1, fontSize: '0.7rem' }}
                              />
                            )}
                          </mui.Box>
                        </li>
                      );
                    }}
                    disableClearable
                    filterOptions={(options, params) => {
                      const filtered = options.filter(option => 
                        option.toLowerCase().includes(params.inputValue.toLowerCase()) &&
                        option !== currentRule.role2 // Exclude role already selected in Role 2
                      );
                      
                      // If input value is not empty and not in the filtered list, allow custom value
                      if (params.inputValue !== '' && !filtered.includes(params.inputValue) && params.inputValue !== currentRule.role2) {
                        filtered.push(params.inputValue);
                      }
                      
                      return filtered;
                    }}
                  />
                  
                  <Autocomplete
                    id="role2-autocomplete"
                    freeSolo
                    options={roleData.roles.map(role => role.role)}
                    value={currentRule.role2}
                    onChange={(event, newValue) => {
                      setCurrentRule({...currentRule, role2: newValue || ''});
                    }}
                    renderInput={(params) => (
                      <mui.TextField
                        {...params}
                        label="Role 2"
                        required
                        margin="normal"
                        variant="outlined"
                        fullWidth
                        error={!currentRule.role2}
                        helperText={!currentRule.role2 ? "Role 2 is required" : ""}
                        sx={{ mb: 2 }}
                      />
                    )}
                    renderOption={(props, option) => {
                      // Find the role data to show user count
                      const roleInfo = roleData.roles.find(r => r.role === option);
                      return (
                        <li {...props}>
                          <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                            <mui.Typography variant="body2">{option}</mui.Typography>
                            {roleInfo && (
                              <mui.Chip 
                                label={`${roleInfo.count} users`} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                                sx={{ ml: 1, fontSize: '0.7rem' }}
                              />
                            )}
                          </mui.Box>
                        </li>
                      );
                    }}
                    disableClearable
                    filterOptions={(options, params) => {
                      const filtered = options.filter(option => 
                        option.toLowerCase().includes(params.inputValue.toLowerCase()) &&
                        option !== currentRule.role1 // Exclude role already selected in Role 1
                      );
                      
                      // If input value is not empty and not in the filtered list, allow custom value
                      if (params.inputValue !== '' && !filtered.includes(params.inputValue) && params.inputValue !== currentRule.role1) {
                        filtered.push(params.inputValue);
                      }
                      
                      return filtered;
                    }}
                  />
                  
                  <mui.FormControl fullWidth margin="normal" sx={{ mb: 2 }}>
                    <mui.InputLabel id="conflict-type-label">Conflict Severity</mui.InputLabel>
                    <mui.Select
                      labelId="conflict-type-label"
                      value={currentRule.conflictType}
                      label="Conflict Severity"
                      onChange={(e) => setCurrentRule({...currentRule, conflictType: e.target.value})}
                    >
                      <mui.MenuItem value="Critical">
                        <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <mui.Box sx={{ 
                            width: 12, 
                            height: 12, 
                            borderRadius: '50%', 
                            bgcolor: '#d32f2f', 
                            mr: 1 
                          }} />
                          Critical
                        </mui.Box>
                      </mui.MenuItem>
                      <mui.MenuItem value="High">
                        <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <mui.Box sx={{ 
                            width: 12, 
                            height: 12, 
                            borderRadius: '50%', 
                            bgcolor: '#ed6c02', 
                            mr: 1 
                          }} />
                          High
                        </mui.Box>
                      </mui.MenuItem>
                      <mui.MenuItem value="Medium">
                        <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <mui.Box sx={{ 
                            width: 12, 
                            height: 12, 
                            borderRadius: '50%', 
                            bgcolor: '#0288d1', 
                            mr: 1 
                          }} />
                          Medium
                        </mui.Box>
                      </mui.MenuItem>
                      <mui.MenuItem value="Low">
                        <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <mui.Box sx={{ 
                            width: 12, 
                            height: 12, 
                            borderRadius: '50%', 
                            bgcolor: '#2e7d32', 
                            mr: 1 
                          }} />
                          Low
                        </mui.Box>
                      </mui.MenuItem>
                    </mui.Select>
                  </mui.FormControl>
                  
                  <mui.TextField
                    label="Description"
                    value={currentRule.description}
                    onChange={(e) => setCurrentRule({...currentRule, description: e.target.value})}
                    fullWidth
                    multiline
                    rows={3}
                    margin="normal"
                    variant="outlined"
                  />

                  {/* Validation alerts */}
                  {currentRule.role1 && currentRule.role2 && currentRule.role1 === currentRule.role2 && (
                    <mui.Alert severity="error" sx={{ mt: 2 }}>
                      Role 1 and Role 2 cannot be the same. Please select different roles.
                    </mui.Alert>
                  )}
                  
                  {!isEditMode && currentRule.role1 && currentRule.role2 && 
                    currentRule.role1 !== currentRule.role2 && 
                    checkRuleExists(currentRule.role1, currentRule.role2) && (
                    <mui.Alert severity="warning" sx={{ mt: 2 }}>
                      A rule for this role combination already exists. Creating a duplicate rule is not recommended.
                    </mui.Alert>
                  )}
                </mui.DialogContent>
                <mui.DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid rgba(0, 0, 0, 0.08)' }}>
                  <mui.Button
                    onClick={() => {
                      setShowRuleForm(false);
                      setCurrentRule(null);
                    }}
                    sx={{
                      color: 'text.secondary',
                      textTransform: 'none',
                    }}
                  >
                    Cancel
                  </mui.Button>
                  <mui.Button
                    onClick={() => handleSaveRule(currentRule)}
                    variant="contained"
                    disableElevation
                    sx={{
                      px: 3,
                      borderRadius: 2,
                      textTransform: 'none',
                    }}
                    disabled={!currentRule.role1 || !currentRule.role2 || 
                      currentRule.role1 === currentRule.role2 || 
                      (!isEditMode && checkRuleExists(currentRule.role1, currentRule.role2))}
                  >
                    {isEditMode ? 'Update Rule' : 'Add Rule'}
                  </mui.Button>
                </mui.DialogActions>
              </>
            )}
          </mui.Dialog>
        </>
      )}
    </div>
  );

  return (
    <ThemeProvider theme={theme}>
      <IASideBar mainContent={customMainContent} />
    </ThemeProvider>
  );
};

export default SODLibrary;
